import { useServices } from '@/app/providers';
import type { GuardRoute, GuardRouteDetail, RouteHistoryParams, User } from '@yawi/types';
import { useCallback, useEffect, useState } from 'react';

export interface GuardRouteFilters {
  guardId?: string;
  /** yyyy-mm-dd en hora local */
  dateFrom?: string;
  dateTo?: string;
}

const PAGE_SIZE = 20;

export function useGuardRoutes() {
  const { guardRepo, userRepo } = useServices();
  const [routes, setRoutes] = useState<GuardRoute[]>([]);
  const [totalPages, setTotalPages] = useState(1);
  const [page, setPage] = useState(1);
  const [filters, setFilters] = useState<GuardRouteFilters>({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [guards, setGuards] = useState<User[]>([]);

  const fetchRoutes = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const params: RouteHistoryParams = {
        page,
        limit: PAGE_SIZE,
        guardId: filters.guardId,
        dateFrom: filters.dateFrom ? new Date(`${filters.dateFrom}T00:00:00`).toISOString() : undefined,
        dateTo: filters.dateTo ? new Date(`${filters.dateTo}T23:59:59.999`).toISOString() : undefined,
      };
      const result = await guardRepo.getRoutes(params);
      setRoutes(result.data);
      setTotalPages(Math.max(1, result.meta.totalPages));
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Error al cargar los recorridos');
    } finally {
      setLoading(false);
    }
  }, [guardRepo, page, filters]);

  useEffect(() => {
    fetchRoutes();
  }, [fetchRoutes]);

  useEffect(() => {
    // ponytail: primeros 100 guardias, paginar el selector si algún tenant tiene más
    userRepo
      .getUsers({ role: 'GUARD', limit: 100 })
      .then((result) => setGuards(result.data))
      .catch(() => setGuards([]));
  }, [userRepo]);

  const changeFilters = useCallback((next: GuardRouteFilters) => {
    setFilters(next);
    setPage(1);
  }, []);

  return {
    routes,
    guards,
    filters,
    page,
    totalPages,
    loading,
    error,
    setPage,
    changeFilters,
    refresh: fetchRoutes,
  };
}

export function useGuardRouteDetail(routeId: string | null) {
  const { guardRepo } = useServices();
  const [route, setRoute] = useState<GuardRouteDetail | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!routeId) {
      setRoute(null);
      return;
    }
    let cancelled = false;
    setLoading(true);
    setError(null);
    guardRepo
      .getRoute(routeId)
      .then((detail) => !cancelled && setRoute(detail))
      .catch((err) => !cancelled && setError(err instanceof Error ? err.message : 'Error al cargar el recorrido'))
      .finally(() => !cancelled && setLoading(false));
    return () => {
      cancelled = true;
    };
  }, [guardRepo, routeId]);

  return { route, loading, error };
}
